/**
 * Outil « pH et acidité » (Boîte à outils, physique-chimie 3e et Tle) — logique PURE.
 *
 * Trois parties :
 * - Échelle (3e, Tle) : solutions de la vie courante sur l'échelle de pH ; acide, neutre,
 *   basique ; ions H⁺ et HO⁻ (3e) ; [H₃O⁺] = c° × 10⁻ᵖᴴ et [HO⁻] = K_e ÷ [H₃O⁺] (Tle, 25 °C).
 * - Dilution (3e, Tle) : un acide fort ou une base forte qu'on dilue dix fois, encore et
 *   encore : le pH se rapproche de 7 sans le dépasser (calcul exact, ions de l'eau compris).
 * - Prédominance (Tle) : pH = pK_A + log([A⁻]/[AH]) ; proportions des formes, indicateur
 *   coloré, acide α-aminé (deux pK_A).
 *
 * Écriture : « pK_A », « K_e » (indices mis en forme par RichText), comme sur les cartes.
 */

import { frNum, mcqOptions, pick, retryMcq, sigFixed, type Mcq, type Rng } from '../../ui/mcq';

export const fr = frNum;
const NB = String.fromCharCode(0xa0);

export type Part = 'scale' | 'dilution' | 'predominance';

export const PARTS: ReadonlyArray<{ id: Part; label: string }> = [
  { id: 'scale', label: 'Échelle' },
  { id: 'dilution', label: 'Dilution' },
  { id: 'predominance', label: 'Prédominance' },
];

/** Tle (ou sans niveau) : concentrations, K_e, pK_A ; avant : ions H⁺ et HO⁻ */
export const advanced = (rank: number | null) => rank === null || rank >= 7;

export function partsFor(rank: number | null) {
  return PARTS.filter((p) => p.id !== 'predominance' || advanced(rank));
}

/** produit ionique de l'eau à 25 °C */
export const KE = 1e-14;

/* ---------- échelle ---------- */

export const SOLUTIONS: ReadonlyArray<{ label: string; pH: number }> = [
  { label: 'Acide de batterie', pH: 0.8 },
  { label: 'Jus de citron', pH: 2.4 },
  { label: 'Vinaigre', pH: 2.9 },
  { label: 'Soda', pH: 3.2 },
  { label: 'Café', pH: 5 },
  { label: 'Eau pure', pH: 7 },
  { label: 'Sang', pH: 7.4 },
  { label: 'Eau de mer', pH: 8.2 },
  { label: 'Eau savonneuse', pH: 10 },
  { label: 'Eau de Javel', pH: 11.5 },
  { label: 'Déboucheur (soude)', pH: 13.5 },
];

export type Nature = 'acide' | 'neutre' | 'basique';
export const natureOf = (pH: number): Nature =>
  Math.abs(pH - 7) < 0.05 ? 'neutre' : pH < 7 ? 'acide' : 'basique';

/** concentrations en mol·L⁻¹ (c° = 1 mol·L⁻¹, 25 °C) */
export const h3o = (pH: number) => 10 ** -pH;
export const ho = (pH: number) => KE / h3o(pH);

/** couleur de l'indicateur universel (papier pH) */
export function universalColor(pH: number): string {
  const stops: Array<[number, [number, number, number]]> = [
    [0, [220, 38, 38]],
    [3, [249, 115, 22]],
    [5, [234, 179, 8]],
    [7, [34, 197, 94]],
    [9, [20, 184, 166]],
    [11, [59, 130, 246]],
    [14, [109, 40, 217]],
  ];
  const p = Math.min(14, Math.max(0, pH));
  for (let i = 0; i < stops.length - 1; i++) {
    const [a, ca] = stops[i]!;
    const [b, cb] = stops[i + 1]!;
    if (p <= b) {
      const t = (p - a) / (b - a);
      const c = ca.map((x, k) => Math.round(x + t * (cb[k]! - x)));
      return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
    }
  }
  return 'rgb(109, 40, 217)';
}

export const corrosive = (pH: number) => pH <= 2 || pH >= 12;

/**
 * Schéma « bécher » de 3e : nombres d'ions H⁺ et HO⁻ dessinés (pas à l'échelle : seul compte
 * lequel est le plus nombreux) ; jamais zéro.
 */
export function ionDots(pH: number): { h: number; ho: number } {
  const n = (d: number) => Math.max(1, Math.min(16, Math.round(4 + 1.8 * d)));
  return { h: n(7 - pH), ho: n(pH - 7) };
}

/* ---------- dilution ---------- */

export type Solute = 'acid' | 'base';

export const DILUTION_START: Record<Solute, { label: string; pH: number }> = {
  acid: { label: 'Acide chlorhydrique', pH: 2 },
  base: { label: 'Soude', pH: 12 },
};
export const MAX_DILUTIONS = 8;

/** pH exact d'un acide fort (ou d'une base forte) de concentration c, ions de l'eau compris */
export function strongPH(kind: Solute, c: number): number {
  const x = c / 2 + Math.sqrt((c * c) / 4 + KE); // [H₃O⁺] (acide) ou [HO⁻] (base)
  return kind === 'acid' ? -Math.log10(x) : 14 + Math.log10(x);
}

export const startConc = (kind: Solute, pH0: number) =>
  kind === 'acid' ? 10 ** -pH0 : 10 ** (pH0 - 14);

/** pH après 0, 1, …, n dilutions au dixième */
export function dilutionSeries(kind: Solute, pH0: number, n: number): number[] {
  const c0 = startConc(kind, pH0);
  return Array.from({ length: n + 1 }, (_, k) => strongPH(kind, c0 / 10 ** k));
}

/* ---------- prédominance ---------- */

export interface Couple {
  id: string;
  label: string;
  /** de la plus acide à la plus basique */
  species: readonly string[];
  pKas: readonly number[];
  /** indicateur coloré : couleur de chaque forme (nom, teinte) */
  colors?: ReadonlyArray<readonly [string, string]>;
  /** teinte du mélange à parts égales (jaune + bleu : vert) */
  mix?: string;
}

export const COUPLES: readonly Couple[] = [
  { id: 'acetic', label: 'Éthanoïque', species: ['CH₃COOH', 'CH₃COO⁻'], pKas: [4.8] },
  { id: 'ammonium', label: 'Ammonium', species: ['NH₄⁺', 'NH₃'], pKas: [9.2] },
  {
    id: 'bbt',
    label: 'BBT',
    species: ['HInd', 'Ind⁻'],
    pKas: [7.1],
    colors: [
      ['jaune', '#eab308'],
      ['bleu', '#2563eb'],
    ],
    mix: '#22a34a',
  },
  {
    id: 'glycine',
    label: 'Glycine',
    species: ['H₃N⁺–CH₂–COOH', 'H₃N⁺–CH₂–COO⁻', 'H₂N–CH₂–COO⁻'],
    pKas: [2.3, 9.6],
  },
];

/** proportions de chaque forme (somme 1) : log w_i = i·pH − Σ_{j<i} pK_A,j */
export function fractions(pH: number, pKas: readonly number[]): number[] {
  const logs = [0];
  for (let i = 0; i < pKas.length; i++) logs.push(logs[i]! + pH - pKas[i]!);
  const m = Math.max(...logs);
  const w = logs.map((l) => 10 ** (l - m));
  const s = w.reduce((a, b) => a + b, 0);
  return w.map((x) => x / s);
}

/** indice de la forme qui prédomine ; null si pH = pK_A (deux formes à égalité) */
export function predominantIndex(pH: number, pKas: readonly number[]): number | null {
  if (pKas.some((k) => Math.abs(pH - k) < 0.05)) return null;
  return pKas.filter((k) => pH > k).length;
}

/**
 * teinte d'un indicateur selon les proportions des deux formes ; les couleurs se mélangent
 * comme des encres (jaune + bleu donnent du vert), d'où la teinte du milieu
 */
export function indicatorColor(pH: number, c: Couple): string | null {
  if (!c.colors) return null;
  const f = fractions(pH, c.pKas)[1]!;
  const rgb = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const a = rgb(c.colors[0]![1]);
  const b = rgb(c.colors[1]![1]);
  const mid = c.mix ? rgb(c.mix) : a.map((x, k) => (x + b[k]!) / 2);
  const [from, to, t] = f < 0.5 ? [a, mid, 2 * f] : [mid, b, 2 * f - 1];
  const m = from.map((x, k) => Math.round(x + t * (to[k]! - x)));
  return `rgb(${m[0]}, ${m[1]}, ${m[2]})`;
}

/* ---------- écriture ---------- */

const SUP = '⁰¹²³⁴⁵⁶⁷⁸⁹';
/** « 10⁻³ » */
export const pow10 = (e: number) =>
  `10${e < 0 ? '⁻' : ''}${String(Math.abs(e)).replace(/\d/g, (d) => SUP[Number(d)]!)}`;

/** « 3,2 × 10⁻⁴ », « 10⁻³ » ; `s` chiffres significatifs */
export function sci(v: number, s = 2): string {
  if (v === 0) return '0';
  let e = Math.floor(Math.log10(Math.abs(v)));
  let m = v / 10 ** e;
  if (Number(Math.abs(m).toPrecision(s)) >= 10) {
    e += 1;
    m /= 10;
  }
  if (Number(Math.abs(m).toPrecision(s)) < 1) {
    e -= 1;
    m *= 10;
  }
  const ms = sigFixed(m, s);
  if (/^1(,0*)?$/.test(ms)) return pow10(e);
  return `${ms}${NB}×${NB}${pow10(e)}`;
}
export const conc = (v: number) => `${sci(v)}${NB}mol·L⁻¹`;
export const phText = (pH: number) => fr(pH, 1);

/* ---------- phrases du prof ---------- */

export function scaleLines(pH: number, rank: number | null): string[] {
  const n = natureOf(pH);
  const very = Math.abs(pH - 7) > 5 ? 'très ' : '';
  const out = [
    n === 'neutre'
      ? 'pH = 7 : solution neutre.'
      : `pH = ${phText(pH)} ${n === 'acide' ? '<' : '>'} 7 : solution ${very}${n}.`,
  ];
  if (advanced(rank)) {
    out.push(`[H₃O⁺] = c° × 10⁻ᵖᴴ = ${conc(h3o(pH))}.`);
    out.push(`À 25 °C, [H₃O⁺] × [HO⁻] = K_e = 10⁻¹⁴ : [HO⁻] = ${conc(ho(pH))}.`);
    out.push('pH qui baisse de 1 : [H₃O⁺] multipliée par 10.');
  } else {
    out.push(
      n === 'acide'
        ? 'Plus d’ions H⁺ que d’ions HO⁻ (il y a quand même quelques ions HO⁻).'
        : n === 'basique'
          ? 'Plus d’ions HO⁻ que d’ions H⁺ (il y a quand même quelques ions H⁺).'
          : 'Autant d’ions H⁺ que d’ions HO⁻.',
    );
    out.push('Plus le pH est petit, plus la solution est acide.');
  }
  if (corrosive(pH)) out.push('Solution corrosive : gants et lunettes de protection.');
  return out;
}

export function dilutionLines(
  kind: Solute,
  series: readonly number[],
  rank: number | null,
): string[] {
  const n = series.length - 1;
  const acid = kind === 'acid';
  if (n === 0)
    return [
      `${DILUTION_START[kind].label} : pH = ${phText(series[0]!)}. Ajoute de l’eau : dilue 10 fois.`,
      'Sécurité : on verse l’acide dans l’eau, jamais l’eau dans l’acide.',
    ];
  const out = [
    `Après ${n} dilution${n > 1 ? 's' : ''} au dixième, pH = ${fr(series[n]!, 2)} : il ${acid ? 'augmente' : 'diminue'} et se rapproche de 7, sans le dépasser.`,
  ];
  if (advanced(rank))
    out.push(
      acid
        ? 'Acide fort : pH = −log(c ÷ c°) ; diluer 10 fois augmente le pH de 1. Très dilué, ce sont les ions de l’eau qui comptent : pH ≈ 7.'
        : 'Base forte : pH = 14 + log(c ÷ c°) ; diluer 10 fois diminue le pH de 1. Très diluée, pH ≈ 7.',
    );
  else
    out.push(
      `Une solution ${acid ? 'acide' : 'basique'} diluée reste ${acid ? 'acide' : 'basique'}.`,
    );
  return out;
}

export function predominanceLines(pH: number, c: Couple): string[] {
  const f = fractions(pH, c.pKas);
  const i = predominantIndex(pH, c.pKas);
  const out: string[] = [];
  if (c.pKas.length === 1) {
    const [a, b] = c.species as [string, string];
    const d = pH - c.pKas[0]!;
    const r = 10 ** d;
    out.push(
      `log([${b}] ÷ [${a}]) = pH − pK_A = ${fr(d, 1)}, donc [${b}] ÷ [${a}] ≈ ${r >= 1000 || r < 0.01 ? sci(r) : fr(r, 2)}.`,
    );
  } else {
    out.push(`Deux couples : pK_A1 = ${phText(c.pKas[0]!)} et pK_A2 = ${phText(c.pKas[1]!)}.`);
  }
  if (i === null) {
    const j = c.pKas.findIndex((k) => Math.abs(pH - k) < 0.05);
    out.push(`pH = pK_A : autant de ${c.species[j]!} que de ${c.species[j + 1]!}.`);
  } else out.push(`${c.species[i]!} prédomine (${fr(100 * f[i]!, 0)} %).`);
  if (c.pKas.some((k) => Math.abs(pH - k) <= 1) && i !== null)
    out.push(
      'Prédominer n’est pas être seul : à moins d’une unité du pK_A, l’autre forme reste nombreuse.',
    );
  if (c.colors)
    out.push(
      `Indicateur : ${c.colors[0]![0]} (forme acide), ${c.colors[1]![0]} (forme basique) ; zone de virage ≈ pK_A ± 1, de ${phText(c.pKas[0]! - 1)} à ${phText(c.pKas[0]! + 1)}.`,
    );
  if (c.id === 'glycine')
    out.push(
      'Entre les deux pK_A, c’est l’amphion H₃N⁺–CH₂–COO⁻ ; la forme neutre H₂N–CH₂–COOH ne prédomine jamais.',
    );
  return out;
}

/* ---------- défi ---------- */

export type QuizKind =
  | 'scale'
  | 'ions'
  | 'compare'
  | 'dilution'
  | 'neutralisation'
  | 'metal'
  | 'ph-from-h'
  | 'h-from-ph'
  | 'strong-acid'
  | 'predominance'
  | 'ratio';

export type Quiz = Mcq<QuizKind>;

export function quizKinds(rank: number | null): QuizKind[] {
  if (rank === null) return ['scale', 'dilution', 'ph-from-h', 'predominance', 'ratio'];
  if (advanced(rank)) return ['ph-from-h', 'h-from-ph', 'strong-acid', 'predominance', 'ratio'];
  return ['scale', 'ions', 'compare', 'dilution', 'neutralisation', 'metal'];
}

export function makeQuiz(kind: QuizKind, rng: Rng): Quiz {
  return retryMcq((r) => buildQuiz(kind, r), rng);
}

type W = readonly [string, string];
const q = (
  kind: QuizKind,
  question: string,
  good: string,
  wrongs: W[],
  explain: string,
  rng: Rng,
): Quiz => ({ kind, question, options: mcqOptions(good, wrongs, rng), explain });

const NATURES: ReadonlyArray<W> = [
  ['Acide', 'Acide : pH inférieur à 7.'],
  ['Neutre', 'Neutre : pH égal à 7.'],
  ['Basique', 'Basique : pH supérieur à 7.'],
];

function buildQuiz(kind: QuizKind, rng: Rng): Quiz {
  switch (kind) {
    case 'scale': {
      const s = pick(
        SOLUTIONS.filter((x) => x.label !== 'Sang'),
        rng,
      );
      const n = natureOf(s.pH);
      const good = n === 'acide' ? 'Acide' : n === 'neutre' ? 'Neutre' : 'Basique';
      return q(
        kind,
        `${s.label} : le papier pH indique ${phText(s.pH)}. La solution est…`,
        good,
        [
          ...NATURES.filter(([t]) => t !== good),
          [
            'Impossible à dire sans pH-mètre',
            'Le papier pH suffit pour dire si une solution est acide, neutre ou basique.',
          ],
        ],
        'pH < 7 : acide ; pH = 7 : neutre ; pH > 7 : basique.',
        rng,
      );
    }
    case 'ions': {
      const acid = rng() < 0.5;
      return q(
        kind,
        `Une solution contient beaucoup plus d’ions ${acid ? 'H⁺' : 'HO⁻'} que d’ions ${acid ? 'HO⁻' : 'H⁺'}. Elle est…`,
        acid ? 'Acide, de pH inférieur à 7' : 'Basique, de pH supérieur à 7',
        [
          acid
            ? [
                'Basique, de pH supérieur à 7',
                'Ce sont les ions HO⁻ en excès qui rendent une solution basique.',
              ]
            : [
                'Acide, de pH inférieur à 7',
                'Ce sont les ions H⁺ en excès qui rendent une solution acide.',
              ],
          ['Neutre, de pH égal à 7', 'Neutre : autant d’ions H⁺ que d’ions HO⁻.'],
          acid
            ? ['Acide, de pH supérieur à 7', 'Une solution acide a un pH inférieur à 7.']
            : ['Basique, de pH inférieur à 7', 'Une solution basique a un pH supérieur à 7.'],
        ],
        'Plus d’ions H⁺ que d’ions HO⁻ : acide (pH < 7) ; plus d’ions HO⁻ : basique (pH > 7) ; autant : neutre.',
        rng,
      );
    }
    case 'compare': {
      const [a, b] = pick(
        [
          [2, 5],
          [3, 6],
          [1, 4],
          [9, 12],
        ] as const,
        rng,
      );
      const base = a > 7;
      return q(
        kind,
        `Une solution A a un pH de ${a}, une solution B un pH de ${b}. Laquelle est la plus ${base ? 'basique' : 'acide'} ?`,
        base ? 'B' : 'A',
        [
          base
            ? ['A', 'Plus le pH est GRAND, plus la solution est basique.']
            : ['B', 'Plus le pH est PETIT, plus la solution est acide.'],
          ['Elles le sont autant', `Leurs pH sont différents : ${a} et ${b}.`],
          base
            ? ['Aucune : ce sont des acides', 'Un pH supérieur à 7 indique une solution basique.']
            : ['Aucune : ce sont des bases', 'Un pH inférieur à 7 indique une solution acide.'],
        ],
        base
          ? `Plus le pH est grand, plus la solution est basique : pH ${b} > pH ${a}.`
          : `Plus le pH est petit, plus la solution est acide : pH ${a} < pH ${b}.`,
        rng,
      );
    }
    case 'dilution': {
      const acid = rng() < 0.6;
      const pH0 = acid ? pick([2, 3, 4], rng) : pick([10, 11, 12], rng);
      const up = acid ? 'Augmente' : 'Diminue';
      return q(
        kind,
        `On dilue beaucoup une solution ${acid ? 'acide' : 'basique'} de pH ${pH0} avec de l’eau. Son pH…`,
        `${up} et se rapproche de 7, sans le dépasser`,
        [
          [
            acid ? 'Diminue' : 'Augmente',
            acid
              ? 'Diluer un acide le rend MOINS acide : le pH augmente.'
              : 'Diluer une base la rend MOINS basique : le pH diminue.',
          ],
          [
            `${up} et dépasse 7 si on dilue assez`,
            `Une solution ${acid ? 'acide' : 'basique'} diluée reste ${acid ? 'acide' : 'basique'} : le pH ne franchit pas 7.`,
          ],
          [
            'Ne change pas',
            `Les ions ${acid ? 'H⁺' : 'HO⁻'} sont moins concentrés : le pH change.`,
          ],
        ],
        `En diluant, les ions ${acid ? 'H⁺' : 'HO⁻'} sont moins concentrés : le pH ${acid ? 'augmente' : 'diminue'} vers 7 sans jamais le dépasser.`,
        rng,
      );
    }
    case 'neutralisation':
      return q(
        kind,
        'On verse de la soude dans de l’acide chlorhydrique. Quelle réaction a lieu ?',
        'H⁺ + HO⁻ → H₂O',
        [
          [
            'Na⁺ + Cl⁻ → NaCl',
            'Les ions Na⁺ et Cl⁻ ne réagissent pas : ils restent en solution (spectateurs).',
          ],
          [
            'H⁺ + Cl⁻ → HCl',
            'Ce sont les ions H⁺ (de l’acide) et HO⁻ (de la base) qui réagissent.',
          ],
          [
            'H₂O → H⁺ + HO⁻',
            'C’est l’inverse : les ions H⁺ et HO⁻ se combinent pour former de l’eau.',
          ],
        ],
        'Acide + base : les ions H⁺ et HO⁻ réagissent pour former de l’eau ; le mélange s’échauffe.',
        rng,
      );
    case 'metal':
      return q(
        kind,
        'On plonge du fer dans de l’acide chlorhydrique : des bulles se forment. Quel est ce gaz ?',
        'Du dihydrogène H₂',
        [
          [
            'Du dioxygène O₂',
            'Le test du dioxygène (bûchette incandescente qui se rallume) est négatif ici.',
          ],
          [
            'Du dioxyde de carbone CO₂',
            'Il n’y a pas de carbone dans les réactifs : ni le fer ni l’acide n’en contiennent.',
          ],
          [
            'De la vapeur d’eau',
            'Le gaz produit détonne à l’approche d’une flamme : c’est le dihydrogène.',
          ],
        ],
        'Fe + 2 H⁺ → Fe²⁺ + H₂ : le dihydrogène détonne à l’approche d’une flamme ; les ions H⁺ sont consommés, le pH augmente.',
        rng,
      );
    case 'ph-from-h': {
      const m = pick([1, 2, 5], rng);
      const e = pick([2, 3, 4, 5], rng);
      const pH = -Math.log10(m * 10 ** -e);
      const cText = `${m === 1 ? '' : `${m} × `}${pow10(-e)}`;
      return q(
        kind,
        `[H₃O⁺] = ${cText} mol·L⁻¹. Quel est le pH ?`,
        fr(pH, 1),
        [
          [fr(-pH, 1), 'pH = −log([H₃O⁺] ÷ c°) : le signe moins rend le pH positif.'],
          m === 1
            ? [fr(e + 1, 0), `−log(${pow10(-e)}) = ${e}.`]
            : [fr(e, 0), `Le facteur ${m} compte : log(${m}) ≈ ${fr(Math.log10(m), 2)}.`],
          [fr(14 - pH, 1), 'C’est 14 − pH : le pH d’une base dont [HO⁻] aurait cette valeur.'],
        ],
        `pH = −log([H₃O⁺] ÷ c°) = −log(${cText}) ≈ ${fr(pH, 1)}.`,
        rng,
      );
    }
    case 'h-from-ph': {
      const pH = pick([2.5, 3.2, 4.7, 8.3, 11.1], rng);
      return q(
        kind,
        `Une solution a un pH de ${phText(pH)}. Que vaut [H₃O⁺] ?`,
        conc(h3o(pH)),
        [
          [conc(10 ** pH), '[H₃O⁺] = c° × 10⁻ᵖᴴ : l’exposant est NÉGATIF.'],
          [conc(ho(pH)), 'C’est [HO⁻] = K_e ÷ [H₃O⁺], pas [H₃O⁺].'],
          [`${fr(pH / 10, 2)}${NB}mol·L⁻¹`, '[H₃O⁺] = c° × 10⁻ᵖᴴ, pas pH ÷ 10.'],
        ],
        `[H₃O⁺] = c° × 10⁻ᵖᴴ = 10^(−${phText(pH)}) ≈ ${conc(h3o(pH))}.`,
        rng,
      );
    }
    case 'strong-acid': {
      const e = pick([1, 2, 3], rng);
      return q(
        kind,
        `On dilue 10 fois une solution d’acide chlorhydrique (acide fort) de pH ${e}. Nouveau pH ?`,
        fr(e + 1, 0),
        [
          [
            fr(e * 10, 0),
            'L’échelle de pH est logarithmique : [H₃O⁺] divisée par 10, pH augmenté de 1.',
          ],
          [fr(e - 1, 0), 'Diluer un acide le rend moins acide : le pH augmente.'],
          [
            fr(e + 0.5, 1),
            'Un pH qui augmente de 0,5 seulement, c’est pour un acide faible ; ici l’acide est fort.',
          ],
        ],
        `Acide fort : pH = −log(c ÷ c°). c divisée par 10 : pH = ${e} + 1 = ${e + 1}.`,
        rng,
      );
    }
    case 'predominance': {
      const c = pick(
        COUPLES.filter((x) => x.pKas.length === 1),
        rng,
      );
      const k = c.pKas[0]!;
      const pH = k + pick([-2, 2, -1.5, 1.5], rng);
      const [a, b] = c.species as [string, string];
      const good = pH < k ? a : b;
      return q(
        kind,
        `Couple ${a}/${b} (pK_A = ${phText(k)}). À pH ${phText(pH)}, quelle forme prédomine ?`,
        good,
        [
          [
            pH < k ? b : a,
            pH < k
              ? 'pH < pK_A : c’est la forme ACIDE qui prédomine.'
              : 'pH > pK_A : c’est la forme BASIQUE qui prédomine.',
          ],
          ['Les deux autant', 'Autant des deux formes seulement quand pH = pK_A.'],
          ['Aucune des deux', 'L’une des deux formes prédomine toujours, sauf à pH = pK_A.'],
        ],
        `pH ${pH < k ? '<' : '>'} pK_A : ${good} prédomine.`,
        rng,
      );
    }
    case 'ratio': {
      const d = pick([1, 2, -1], rng);
      const show = (v: number) => (v >= 1 ? fr(v, 0) : fr(v, 2));
      return q(
        kind,
        `À pH = pK_A ${d > 0 ? '+' : '−'} ${Math.abs(d)}, que vaut le rapport [A⁻] ÷ [AH] ?`,
        show(10 ** d),
        [
          [
            show(10 ** -d),
            'pH = pK_A + log([A⁻] ÷ [AH]) : log du rapport = pH − pK_A, avec son signe.',
          ],
          [fr(d, 0), 'C’est le LOGARITHME du rapport qui vaut pH − pK_A.'],
          ['1', 'Le rapport vaut 1 seulement à pH = pK_A.'],
          [show(10 ** (d + 1)), 'log du rapport = pH − pK_A : refais le calcul.'],
        ],
        `log([A⁻] ÷ [AH]) = pH − pK_A = ${fr(d, 0)}, donc [A⁻] ÷ [AH] = ${show(10 ** d)}.`,
        rng,
      );
    }
  }
}
